/**
 * Staff page CMS payload stored in site_staff_banners.page_config (jsonb).
 * Banner fields remain columns; this holds editorial + feature toggles for each route prefix.
 */

import React from 'react';
import type { ReactNode } from 'react';

export interface SitePageBlogTeaser {
  id: string;
  title: string;
  excerpt: string;
  href: string;
}

export interface SitePageSpotlight {
  title: string;
  subtitle: string;
  body: string;
  imageUrl: string;
  ctaLabel: string;
  ctaHref: string;
}

export interface SitePageFeatureDef {
  readonly key: string;
  readonly label: string;
  readonly description: string;
  readonly group: 'Discovery' | 'Engagement' | 'Merch & drops' | 'Community' | 'Programs' | 'System';
}

/** Thirty configurable page capabilities (stored as booleans; wire incrementally on each route). */
export const SITE_PAGE_FEATURE_DEFS: readonly SitePageFeatureDef[] = [
  { key: 'stats_pulse_strip', label: 'Stats pulse strip', description: 'Animated headline stats under hero.', group: 'Discovery' },
  { key: 'trending_rail', label: 'Trending rail', description: 'Horizontal trending items carousel.', group: 'Discovery' },
  { key: 'creator_spotlight_slot', label: 'Creator spotlight', description: 'Featured creator / seller card.', group: 'Engagement' },
  { key: 'community_join_cta', label: 'Community join CTA', description: 'Prominent join / follow module.', group: 'Community' },
  { key: 'newsletter_inline', label: 'Inline newsletter', description: 'Email capture block in page flow.', group: 'Engagement' },
  { key: 'social_proof_line', label: 'Social proof line', description: 'Live counts / testimonials ticker.', group: 'Engagement' },
  { key: 'seasonal_theme_accent', label: 'Seasonal accent', description: 'Seasonal color / badge accents.', group: 'Discovery' },
  { key: 'countdown_banner_style', label: 'Countdown banner style', description: 'Time-boxed urgency styling on banner.', group: 'Merch & drops' },
  { key: 'blog_teaser_row', label: 'Blog teaser row', description: 'Show configured blog cards below fold.', group: 'Programs' },
  { key: 'staff_picks_module', label: 'Staff picks module', description: 'Curated picks grid or list.', group: 'Merch & drops' },
  { key: 'new_arrival_badges', label: 'New arrival badges', description: '“New” chips on fresh listings.', group: 'Merch & drops' },
  { key: 'explore_dense_grid', label: 'Dense explore grid', description: 'Tighter grid default on Explore.', group: 'Discovery' },
  { key: 'clips_hover_preview', label: 'Clips hover preview', description: 'Preview on hover in clips lists.', group: 'Discovery' },
  { key: 'drops_calendar_widget', label: 'Drops calendar widget', description: 'Inline calendar for release dates.', group: 'Merch & drops' },
  { key: 'market_watchlist_promo', label: 'Market watchlist promo', description: 'Watchlist signup on Market.', group: 'Merch & drops' },
  { key: 'culture_editorial_layout', label: 'Culture editorial layout', description: 'Magazine-style section headers.', group: 'Community' },
  { key: 'community_events_rail', label: 'Community events rail', description: 'Upcoming events strip.', group: 'Community' },
  { key: 'sell_pricing_guide', label: 'Sell pricing guide', description: 'Consignment / pricing helper block.', group: 'Merch & drops' },
  { key: 'features_comparison_table', label: 'Features comparison', description: 'Product / tier comparison table.', group: 'Programs' },
  { key: 'learn_progress_badges', label: 'Learn progress badges', description: 'Course progress indicators.', group: 'Programs' },
  { key: 'vault_rarity_story', label: 'Vault rarity story', description: 'Rarity explainer / lore block.', group: 'Programs' },
  { key: 'radar_alert_strip', label: 'Radar alert strip', description: 'Drop radar notifications strip.', group: 'Merch & drops' },
  { key: 'lab_beta_ribbon', label: 'Lab beta ribbon', description: 'Experimental features ribbon.', group: 'Programs' },
  { key: 'heart_donation_cta', label: 'Heart donation CTA', description: 'Charity / impact callout.', group: 'Programs' },
  { key: 'ai_stylist_sticky', label: 'AI stylist sticky CTA', description: 'Sticky “Try AI” on stylist routes.', group: 'Programs' },
  { key: 'grow_referral_widget', label: 'Grow referral widget', description: 'Referral / ambassador widget.', group: 'Programs' },
  { key: 'reduced_motion_safe', label: 'Reduced motion safe', description: 'Respect prefers-reduced-motion.', group: 'System' },
  { key: 'footer_extra_promo', label: 'Extra footer promo', description: 'Secondary promo in footer area.', group: 'System' },
  { key: 'staff_qa_watermark', label: 'Staff QA watermark', description: 'Light watermark for QA builds.', group: 'System' },
] as const;

export type SitePageFeatureKey = (typeof SITE_PAGE_FEATURE_DEFS)[number]['key'];

export interface SitePageConfig {
  /** When true, show the editorial strip (tagline, body, blogs, spotlight) on the live site. */
  showContentStrip: boolean;
  pageTagline: string;
  /** Plain text or light markdown (newlines; **bold**). */
  pageBodyMd: string;
  blogs: SitePageBlogTeaser[];
  spotlight: SitePageSpotlight;
  features: Record<string, boolean>;
}

export const emptySpotlight = (): SitePageSpotlight => ({
  title: '',
  subtitle: '',
  body: '',
  imageUrl: '',
  ctaLabel: '',
  ctaHref: '',
});

export const defaultSitePageConfig = (): SitePageConfig => ({
  showContentStrip: false,
  pageTagline: '',
  pageBodyMd: '',
  blogs: [],
  spotlight: emptySpotlight(),
  features: Object.fromEntries(SITE_PAGE_FEATURE_DEFS.map((d) => [d.key, false])),
});

function isRecord(x: unknown): x is Record<string, unknown> {
  return !!x && typeof x === 'object' && !Array.isArray(x);
}

export function mergePageConfig(raw: unknown): SitePageConfig {
  const base = defaultSitePageConfig();
  if (!isRecord(raw)) return base;

  const featuresIn = isRecord(raw.features) ? raw.features : {};
  const features = { ...base.features };
  for (const d of SITE_PAGE_FEATURE_DEFS) {
    if (typeof featuresIn[d.key] === 'boolean') features[d.key] = featuresIn[d.key] as boolean;
  }

  let blogs: SitePageBlogTeaser[] = [];
  if (Array.isArray(raw.blogs)) {
    blogs = raw.blogs
      .map((b, i) => {
        if (!isRecord(b)) return null;
        const id = typeof b.id === 'string' && b.id ? b.id : `blog-${i}-${Date.now()}`;
        return {
          id,
          title: typeof b.title === 'string' ? b.title : '',
          excerpt: typeof b.excerpt === 'string' ? b.excerpt : '',
          href: typeof b.href === 'string' ? b.href : '',
        };
      })
      .filter(Boolean) as SitePageBlogTeaser[];
  }

  let spotlight = emptySpotlight();
  if (isRecord(raw.spotlight)) {
    const sp = raw.spotlight;
    spotlight = {
      title: typeof sp.title === 'string' ? sp.title : '',
      subtitle: typeof sp.subtitle === 'string' ? sp.subtitle : '',
      body: typeof sp.body === 'string' ? sp.body : '',
      imageUrl:
        typeof sp.imageUrl === 'string'
          ? sp.imageUrl
          : typeof sp.image_url === 'string'
            ? sp.image_url
            : '',
      ctaLabel: typeof sp.ctaLabel === 'string' ? sp.ctaLabel : typeof sp.cta_label === 'string' ? sp.cta_label : '',
      ctaHref: typeof sp.ctaHref === 'string' ? sp.ctaHref : typeof sp.cta_href === 'string' ? sp.cta_href : '',
    };
  }

  return {
    showContentStrip: raw.showContentStrip === true,
    pageTagline: typeof raw.pageTagline === 'string' ? raw.pageTagline : typeof raw.page_tagline === 'string' ? raw.page_tagline : '',
    pageBodyMd:
      typeof raw.pageBodyMd === 'string'
        ? raw.pageBodyMd
        : typeof raw.page_body_md === 'string'
          ? raw.page_body_md
          : '',
    blogs,
    spotlight,
    features,
  };
}

/** Persistable JSON (camelCase) for Supabase jsonb. */
export function serializePageConfig(c: SitePageConfig): Record<string, unknown> {
  return {
    showContentStrip: c.showContentStrip,
    pageTagline: c.pageTagline,
    pageBodyMd: c.pageBodyMd,
    blogs: c.blogs,
    spotlight: c.spotlight,
    features: c.features,
  };
}

export function hasEditorialContent(c: SitePageConfig): boolean {
  if (c.pageTagline.trim()) return true;
  if (c.pageBodyMd.trim()) return true;
  if (c.blogs.some((b) => b.title.trim() || b.excerpt.trim())) return true;
  if (c.spotlight.title.trim() || c.spotlight.body.trim()) return true;
  return false;
}

/** Minimal inline “markdown”: newlines + **bold**. */
export function renderSimpleMd(text: string): ReactNode {
  const lines = text.split('\n');
  return lines.map((line, li) =>
    React.createElement(
      'p',
      { key: li, className: 'text-sm text-gray-300 leading-relaxed mb-2 last:mb-0' },
      line.split(/(\*\*[^*]+\*\*)/g).map((part, pi) => {
        const m = part.match(/^\*\*(.+)\*\*$/);
        if (m) {
          return React.createElement('strong', { key: pi, className: 'text-white font-semibold' }, m[1]);
        }
        return React.createElement('span', { key: pi }, part);
      })
    )
  );
}
